import QRCode from 'qrcode';
import { fitSize, rgbaToGray, toBitmap } from '@shripos/shared/escpos';
import { API_ORIGIN } from '../api.js';

/**
 * Browser-side image work for ESC/POS: text the printer's code page can't print (₹, Telugu,
 * Hindi…) is drawn on a canvas and sent as a raster; logos are dithered; QR codes can be
 * rasterised for printers without native QR support.
 */
const FONT_STACK =
  '"Noto Sans Mono", Consolas, "DejaVu Sans Mono", "Nirmala UI", "Noto Sans Telugu", "Noto Sans Devanagari", monospace';

function canvas(w, h) {
  const c =
    typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(w, h)
      : Object.assign(document.createElement('canvas'), { width: w, height: h });
  const ctx = c.getContext('2d', { willReadFrequently: true });
  return { c, ctx };
}

/** One layout row → bitmap exactly profile.dotWidth wide (see renderEscPos rasterizeText hook). */
export function rasterizeText(row, profile) {
  const cell = profile.dotWidth / profile.charsPerLine;
  const fontPx = Math.max(10, Math.round(cell / 0.6));
  const lineH = Math.round(fontPx * 1.3);
  const w = profile.dotWidth;
  const h = lineH * row.h;
  const { ctx } = canvas(w, h);
  ctx.fillStyle = row.invert ? '#000' : '#fff';
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = row.invert ? '#fff' : '#000';
  ctx.textBaseline = 'middle';
  ctx.font = `${row.bold ? '700 ' : ''}${fontPx}px ${FONT_STACK}`;
  const avail = w / row.w;
  const measured = ctx.measureText(row.text).width;
  ctx.save();
  ctx.scale(row.w * (measured > avail ? avail / measured : 1), row.h);
  ctx.fillText(row.text, 0, lineH / 2);
  ctx.restore();
  const gray = rgbaToGray(ctx.getImageData(0, 0, w, h).data, w, h);
  return toBitmap(gray, w, h, { method: 'threshold', threshold: 150 });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Logo could not be loaded'));
    img.src = src;
  });
}

const logoCache = new Map();

/** Business logo → dithered bitmap, at most 60% of the paper width and ~2 cm tall. */
export async function logoBitmap(logoUrl, profile) {
  if (!logoUrl) return null;
  const key = `${logoUrl}|${profile.dotWidth}`;
  if (!logoCache.has(key)) {
    logoCache.set(
      key,
      (async () => {
        const img = await loadImage(logoUrl.startsWith('http') ? logoUrl : `${API_ORIGIN}${logoUrl}`);
        const { width, height } = fitSize(
          img.naturalWidth,
          img.naturalHeight,
          Math.round(profile.dotWidth * 0.6),
          profile.dotWidth > 400 ? 160 : 120,
        );
        const { ctx } = canvas(width, height);
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        return toBitmap(rgbaToGray(ctx.getImageData(0, 0, width, height).data, width, height), width, height);
      })().catch((err) => {
        logoCache.delete(key);
        throw err;
      }),
    );
  }
  return logoCache.get(key);
}

/** QR module matrix for qrMode "raster" (and the on-screen preview). */
export function qrMatrix(data) {
  const q = QRCode.create(data, { errorCorrectionLevel: 'M' });
  return { size: q.modules.size, get: (x, y) => Boolean(q.modules.get(y, x)) };
}

/** Draw a 1-bit bitmap onto a visible canvas element (preview). */
export function paintBitmap(el, bmp, scale = 1) {
  el.width = bmp.width;
  el.height = bmp.height;
  const ctx = el.getContext('2d');
  const img = ctx.createImageData(bmp.width, bmp.height);
  const rowBytes = Math.ceil(bmp.width / 8);
  for (let y = 0; y < bmp.height; y++)
    for (let x = 0; x < bmp.width; x++) {
      const on = bmp.data[y * rowBytes + (x >> 3)] & (0x80 >> (x & 7));
      const i = (y * bmp.width + x) * 4;
      const v = on ? 0 : 255;
      img.data[i] = v;
      img.data[i + 1] = v;
      img.data[i + 2] = v;
      img.data[i + 3] = 255;
    }
  ctx.putImageData(img, 0, 0);
  el.style.width = `${(bmp.width * scale).toFixed(0)}px`;
}
